let _ = t => t,
  _t;
import { useContext, createContext, memo, useCallback, useMemo, useState } from 'react';
import { View } from 'foundations-components/components/View';
import styledWithTheme from 'foundations-theming/utils';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { RemoveIcon } from 'foundations-assets';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { ObjectSearchFilterEditor } from '../../rewrite/filters/components/objectSearchFilterEditor/ObjectSearchFilterEditor';
import { INDEX_PAGE_INLINE_SIDEBAR_WIDTH } from './IndexPage';

// Create sidebar context
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const SidebarContext = /*#__PURE__*/createContext({
  activeSidebarType: null,
  openSidebar: () => {},
  closeSidebar: () => {},
  sidebarWidth: 'auto'
});

// Sidebar provider component to manage sidebar state
export const IndexPageInlineSidebarProvider = ({
  children
}) => {
  const [activeSidebarType, setActiveSidebarType] = useState(null);
  const openSidebar = useCallback(sidebarType => {
    setActiveSidebarType(prevType =>
    // If the same sidebar type is already open, close it
    prevType === sidebarType ? null : sidebarType);
  }, []);
  const closeSidebar = useCallback(() => {
    setActiveSidebarType(null);
  }, []);
  const value = useMemo(() => ({
    activeSidebarType,
    openSidebar,
    closeSidebar,
    sidebarWidth: INDEX_PAGE_INLINE_SIDEBAR_WIDTH
  }), [activeSidebarType, openSidebar, closeSidebar]);
  return /*#__PURE__*/_jsx(SidebarContext.Provider, {
    value: value,
    children: children
  });
};

// Custom hook to use the sidebar context
export const useIndexPageInlineSidebar = () => useContext(SidebarContext);
const SidebarContainer = styledWithTheme.div(_t || (_t = _`
  width: ${0};
  transition: width 0.15s cubic-bezier(0.8, 0, 1, 1);
  height: 100%;
`), ({
  sidebarIsOpen,
  sidebarWidth
}) => !sidebarIsOpen ? '0' : sidebarWidth);
const IndexPageInlineSidebar = /*#__PURE__*/memo(({
  sidebars
}) => {
  const {
    activeSidebarType,
    closeSidebar,
    sidebarWidth
  } = useIndexPageInlineSidebar();
  const activeSidebar = useMemo(() => sidebars.find(sidebar => sidebar.sidebarType === activeSidebarType), [sidebars, activeSidebarType]);
  return /*#__PURE__*/_jsx(SidebarContainer, {
    sidebarIsOpen: !!activeSidebarType,
    sidebarWidth: sidebarWidth,
    children: /*#__PURE__*/_jsx(View, {
      borderWidth: "100",
      borderColor: "border-core-subtle",
      borderStyle: "solid",
      borderRadius: "100",
      backgroundColor: "fill-surface-default",
      blockSize: "100%",
      marginInlineStart: "300",
      overflowY: "auto",
      children: activeSidebar && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Flex, {
          paddingInline: "900",
          paddingBlock: "600",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsx(Text, {
            typography: "heading-300",
            textTransform: "capitalize",
            children: activeSidebar.title
          }), /*#__PURE__*/_jsx(IconButton, {
            use: "transparent",
            size: "sm",
            "aria-label": unescapedText('indexPage.indexPageInlineSidebar.closeButton'),
            onClick: closeSidebar,
            children: /*#__PURE__*/_jsx(RemoveIcon, {})
          })]
        }), activeSidebar.content]
      })
    })
  });
});
export const createSidebars = pageType => [{
  sidebarType: 'settings',
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `indexPage.indexPageInlineSidebar.titles.${pageType !== null && pageType !== void 0 ? pageType : 'unknown'}`
  }),
  content: /*#__PURE__*/_jsx(View, {
    paddingInline: "900",
    paddingBlock: "400",
    children: "settings content"
  })
}, {
  sidebarType: 'filters',
  title: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "indexPage.moreFilters.header"
  }),
  content: /*#__PURE__*/_jsx(View, {
    paddingInline: "500",
    children: /*#__PURE__*/_jsx(ObjectSearchFilterEditor, {
      layout: "embedded"
    })
  })
}, {
  sidebarType: 'preview',
  title: 'Preview Title',
  content: /*#__PURE__*/_jsx("div", {
    children: "preview content"
  })
}];
IndexPageInlineSidebar.displayName = 'IndexPageInlineSidebar';
export default IndexPageInlineSidebar;